/**
 * * Accès aux variables d'environnement de build, sans valeur de repli.
 *
 * * Voir AGENTS.md à la racine du dépôt, section « Configuration : jamais de
 * * valeur de repli ». En résumé : un repli vers une URL de production rend la
 * * variable oubliée invisible — le Studio de dev se met à parler au Core de
 * * prod sans qu'aucune erreur ne le signale. Un repli vers localhost a le même
 * * défaut sous une forme plus discrète : sur un poste de développement il
 * * fonctionne, et il ne casse qu'une fois déployé.
 *
 * * Particularité de Vite : ces valeurs sont substituées à la compilation, pas
 * * lues à l'exécution. Une variable absente est donc une erreur de build, et
 * * l'application construite est irrémédiablement mal configurée. On lève au
 * * chargement du module pour que ce soit visible tout de suite, plutôt qu'au
 * * premier clic sur le lien concerné.
 */

function requireViteEnv(name: string): string {
  const value = (import.meta.env as Record<string, string | undefined>)[name];
  if (value === undefined || value.trim() === '') {
    throw new Error(
      `Variable de build manquante : ${name}. ATF_Studio a été construit sans ` +
      `elle et ne peut pas deviner à quel environnement s'adresser. ` +
      `Renseigner la variable puis reconstruire — voir ATF_Core/ENVIRONMENT.md.`
    );
  }
  return value;
}

function requireViteUrlEnv(name: string, protocols: string[]): string {
  const value = requireViteEnv(name);
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error(
      `Variable de build ${name} invalide : "${value}" n'est pas une URL absolue.`
    );
  }
  if (!protocols.includes(parsed.protocol)) {
    throw new Error(
      `Variable de build ${name} invalide : protocole "${parsed.protocol}" ` +
      `inattendu, attendu ${protocols.join(' ou ')}.`
    );
  }
  return value;
}

/**
 * * URL publique d'ATF_Core, sans le suffixe /api ni la barre oblique finale.
 * * Sert à composer les liens vers les agents routés par Traefik.
 */
export function coreBaseUrl(): string {
  return requireViteUrlEnv('VITE_CORE_API_URL', ['http:', 'https:'])
    .replace(/\/api\/?$/, '')
    .replace(/\/$/, '');
}

/**
 * * URL WebSocket du serveur de signaling, transmise aux agents créés.
 */
export function signalingWsUrl(): string {
  return requireViteUrlEnv('VITE_SIGNALING_WS_URL', ['ws:', 'wss:']);
}
